import { Box, Grid, MenuItem, OutlinedInput, Select, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { FormField } from '../../form/FormField';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { FC, useEffect } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { EditPersonaAvatar } from './EditPersonaAvatar';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../utils/useDebounceEffect';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { ControlledFormField } from '../../form/ControlledFormField';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { useSnackbar } from '../../store/snackbar';

const getPersonaQuery = graphql(`
	query personaDetailEditDrawer_getPersona($personaId: UUID!) {
		getPersona(by: { id: $personaId }) {
			age
			description
			gender
			location
			id
			job
			name
			quote
			status
			color
			...PersonaAvatar_persona
		}
	}
`);

const updatePersonaDetailMutation = graphql(`
	mutation updatePersonaDetailMutation($updatePersonaBy2: PersonaUniqueWhere!, $data: PersonaUpdateInput!) {
		updatePersona(by: $updatePersonaBy2, data: $data) {
			ok
			errorMessage
		}
	}
`);

export const personaDetailEditDrawerSchema = z.object({
	personaId: z.string(),
});

const personaEditFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'form.edit-persona-detail.error.empty-name' })),
		quote: z.string().nullable(),
		description: z.string().nullable(),
		age: z
			.string()
			.nullable()
			.refine(
				(value) => {
					if (value === '' || value === null) {
						return true;
					}
					if (isNaN(Number(value))) {
						return false;
					}
					const age = parseInt(value);
					return age >= 0 && age <= 110;
				},
				intl.formatMessage({ id: 'form.edit-persona-detail.error.invalid-age' }),
			),
		gender: z.enum(['female', 'male']).nullable(),
		status: z.enum(['divorced', 'married', 'single', 'widow']).nullable(),
		job: z.string().nullable(),
		location: z.string().nullable(),
	});

export const PersonaDetailEditDrawer: FC<z.infer<typeof personaDetailEditDrawerSchema>> = ({ personaId }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { data, isPending, refetch } = useApiQuery(getPersonaQuery, {
		variables: { personaId },
		enabled: false,
	});

	useEffect(() => {
		refetch();
	}, [refetch]);

	const { mutate } = useApiMutation(updatePersonaDetailMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updatePersona.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'drawer.edit-persona-detail.update.success' }),
					error: intl.formatMessage({ id: 'drawer.edit-persona-detail.update.error' }),
				},
				errorMessage: result.updatePersona.errorMessage,
				openSnackbar,
			}),
	});

	const form = useTypedForm({
		schema: personaEditFormSchema(intl),
		mode: 'all',
		reValidateMode: 'onChange',
	});

	useEffect(() => {
		if (!isPending && data) {
			form.reset({
				name: data.getPersona?.name ?? '',
				quote: data.getPersona?.quote ?? '',
				description: data.getPersona?.description ?? null,
				age: data.getPersona?.age?.toString() ?? '0',
				gender: data.getPersona?.gender ?? 'female',
				status: data.getPersona?.status ?? 'single',
				job: data.getPersona?.job ?? '',
				location: data.getPersona?.location ?? '',
			});
		}
	}, [isPending, data, form]);

	const { name, quote, description, job, age, gender, status, location } = form.watch();
	const { isValid, isDirty } = form.formState;

	useDebounceEffect(() => {
		if (isValid && isDirty) {
			mutate({
				updatePersonaBy2: { id: personaId },
				data: { name, quote, description, job, age: Number(age), gender, status, location },
			});
			form.reset({
				name,
				quote,
				description,
				age,
				gender,
				status,
				job,
				location,
			});
		}
	}, [name, quote, description, job, age, gender, status, location, isValid, personaId, mutate, isDirty]);

	const personaStatusOptions = [
		{
			value: 'single',
			label: intl.formatMessage({ id: 'persona.detail.bio.status-single' }),
		},
		{
			value: 'married',
			label: intl.formatMessage({ id: 'persona.detail.bio.status-married' }),
		},
		{
			value: 'divorced',
			label: intl.formatMessage({ id: 'persona.detail.bio.status-divorced' }),
		},
		{
			value: 'widow',
			label: intl.formatMessage({ id: 'persona.detail.bio.status-widow' }),
		},
	];

	const personaGenderOptions = [
		{
			value: 'female',
			label: intl.formatMessage({ id: 'persona.detail.bio.gender-female' }),
		},
		{
			value: 'male',
			label: intl.formatMessage({ id: 'persona.detail.bio.gender-male' }),
		},
	];

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'drawer.edit-persona-detail.title' })} />
			<Box width={'100%'}>
				<form noValidate>
					<Stack gap={3}>
						<Stack spacing={1}>
							<EditPersonaAvatar personaId={personaId} />
						</Stack>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.fullname' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<FormField
							form={form}
							name={'quote'}
							label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.quote' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<FormField
							form={form}
							name={'description'}
							label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.description' })}
							render={(props) => <TextField {...props} rows={5} multiline />}
						/>
						<Grid container direction={'row'} spacing={2}>
							<Grid item xs={4}>
								<FormField
									form={form}
									name={'age'}
									label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.age' })}
									render={(props) => <TextField {...props} type={'number'} inputProps={{ inputMode: 'numeric', max: 110, min: 0 }} />}
								/>
							</Grid>
							<Grid item xs={6} md={4}>
								<ControlledFormField
									form={form}
									name={'gender'}
									label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.gender' })}
									render={(props) =>
										data?.getPersona?.gender ? (
											<ToggleButtonGroup
												size={'medium'}
												sx={{
													'.MuiToggleButton-root': {
														py: 1.1,
														px: 1.5,
													},
													'.MuiToggleButton-root.Mui-selected': {
														background: 'none',
														outline: (theme) =>
															`1px solid ${getNightOrDayPalette(theme) === 'night' ? theme.palette.primary.main : theme.palette.day.A100}`,
														outlineOffset: -1,
													},
												}}
												exclusive
												{...props}
											>
												{personaGenderOptions.map((gender, key) => (
													<ToggleButton value={gender.value} key={key}>
														{gender.label}
													</ToggleButton>
												))}
											</ToggleButtonGroup>
										) : (
											<></>
										)
									}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormField
									form={form}
									name={'status'}
									label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.status' })}
									render={(props) =>
										data?.getPersona?.gender ? (
											<Select {...props} size={'medium'} defaultValue={data.getPersona.status}>
												{personaStatusOptions.map((status, key) => (
													<MenuItem value={status.value} key={key}>
														{status.label}
													</MenuItem>
												))}
											</Select>
										) : (
											<></>
										)
									}
								/>
							</Grid>
						</Grid>
						<FormField
							form={form}
							name={'job'}
							label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.job' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<FormField
							form={form}
							name={'location'}
							label={intl.formatMessage({ id: 'drawer.edit-persona-detail.label.location' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
					</Stack>
				</form>
			</Box>
		</DrawerWrapper>
	);
};
